import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  InputAdornment,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import PercentIcon from "@mui/icons-material/Percent";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { generateCouponCode } from "../../i18n";
const CouponForm = ({ open, onClose, data, onChange, onSubmit }) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};

    if (!data?.code) newErrors.code = t("Code is required");
    if (!data?.startDate) newErrors.startDate = t("Start date is required");
    if (!data?.endDate) newErrors.endDate = t("End date is required");
    if (
      data?.startDate &&
      data?.endDate &&
      new Date(data.startDate) > new Date(data.endDate)
    ) {
      newErrors.endDate = t("End date must be after start date");
    }

    if (data?.discountPercentage < 0 || data?.discountPercentage > 100) {
      newErrors.discountPercentage = t("Discount must be between 0 and 100");
    }

    if (data?.minOrderValue < 0)
      newErrors.minOrderValue = t("Value must be >= 0");
    if (data?.usageLimit < 0)
      newErrors.usageLimit = t("Usage limit must be >= 0");

    if (data?.discountAmount < 0) newErrors.discountAmount = t("Must be >= 0");
    if (data?.maxDiscountAmount < 0)
      newErrors.maxDiscountAmount = t("Must be >= 0");
    if (
      data?.discountAmount != null &&
      data?.maxDiscountAmount != null &&
      data.maxDiscountAmount < data.discountAmount
    ) {
      newErrors.maxDiscountAmount = t("Must be >= discount amount");
    }
    if (data?.productLimit < 0)
      newErrors.productLimit = t("Product limit must be >= 0");

    if (!data?.type) newErrors.type = t("Type is required");
    if (!["active", "inactive"].includes(data?.status)) {
      newErrors.status = t("Invalid status");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) onSubmit();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {data?.couponId ? t("Update Coupon") : t("Create New Coupon")}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("Code")}
                value={data?.code || ""}
                onChange={(e) => onChange("code", e.target.value)}
                error={!!errors.code}
                helperText={
                  errors.code ||
                  t("A unique code for the coupon (e.g. SUMMER2025)")
                }
                fullWidth
              />
              <Box mt={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => onChange("code", generateCouponCode())}
                >
                  {t("Generate Random Code")}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t("Discount Percentage")}
                type="number"
                value={data?.discountPercentage || ""}
                onChange={(e) =>
                  onChange("discountPercentage", Number(e.target.value))
                }
                error={!!errors.discountPercentage}
                helperText={
                  errors.discountPercentage ||
                  t("Percentage off the total order (0–100%)")
                }
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={t("Percentage discount")}>
                        {" "}
                        <PercentIcon fontSize="small" />{" "}
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t("Discount Amount")}
                type="number"
                value={data?.discountAmount || ""}
                onChange={(e) =>
                  onChange("discountAmount", Number(e.target.value))
                }
                error={!!errors.discountAmount}
                helperText={
                  errors.discountAmount ||
                  t("Fixed amount discount to apply (e.g. 50)")
                }
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t("Max Discount Amount")}
                type="number"
                value={data?.maxDiscountAmount || ""}
                onChange={(e) =>
                  onChange("maxDiscountAmount", Number(e.target.value))
                }
                error={!!errors.maxDiscountAmount}
                helperText={
                  errors.maxDiscountAmount ||
                  t("Maximum discount value allowed (if applicable)")
                }
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("Số lượng sản phẩm áp dụng")}
                type="number"
                value={data?.productLimit || ""}
                onChange={(e) =>
                  onChange("productLimit", Number(e.target.value))
                }
                error={!!errors.productLimit}
                helperText={
                  errors.productLimit ||
                  t("Maximum number of products this coupon applies to")
                }
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={t("Limit of products eligible per use")}>
                        <InfoOutlinedIcon fontSize="small" />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t("Start Date")}
                type="datetime-local"
                value={
                  data?.startDate
                    ? new Date(data.startDate).toISOString().slice(0, 16)
                    : ""
                }
                onChange={(e) =>
                  onChange("startDate", new Date(e.target.value).toISOString())
                }
                error={!!errors.startDate}
                helperText={
                  errors.startDate ||
                  t("Date and time the coupon becomes active")
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t("End Date")}
                type="datetime-local"
                value={
                  data?.endDate
                    ? new Date(data.endDate).toISOString().slice(0, 16)
                    : ""
                }
                onChange={(e) =>
                  onChange("endDate", new Date(e.target.value).toISOString())
                }
                error={!!errors.endDate}
                helperText={
                  errors.endDate || t("Date and time the coupon expires")
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t("Min Order Value")}
                type="number"
                value={data?.minOrderValue || ""}
                onChange={(e) =>
                  onChange("minOrderValue", Number(e.target.value))
                }
                error={!!errors.minOrderValue}
                helperText={
                  errors.minOrderValue ||
                  t("Minimum order amount to use this coupon")
                }
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t("Usage Limit")}
                type="number"
                value={data?.usageLimit || ""}
                onChange={(e) => onChange("usageLimit", Number(e.target.value))}
                error={!!errors.usageLimit}
                helperText={
                  errors.usageLimit ||
                  t("Maximum number of times this coupon can be used")
                }
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={t("Total allowed uses")}>
                        {" "}
                        <InfoOutlinedIcon fontSize="small" />{" "}
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={t("Note")}
                value={data?.note || ""}
                onChange={(e) => onChange("note", e.target.value)}
                fullWidth
                multiline
                rows={2}
                helperText={t("Internal notes or description for this coupon")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t("Type")}
                value={data?.type || ""}
                onChange={(e) => onChange("type", e.target.value)}
                error={!!errors.type}
                helperText={
                  errors.type || t("E.g. percentage, fixed, shipping")
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={t("Status")}
                value={data?.status || ""}
                onChange={(e) => onChange("status", e.target.value)}
                error={!!errors.status}
                helperText={
                  errors.status || t("Select whether the coupon is active")
                }
                fullWidth
              >
                <MenuItem value="active">{t("Active")}</MenuItem>
                <MenuItem value="inactive">{t("Inactive")}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={data?.isBirthday || false}
                    onChange={(e) => onChange("isBirthday", e.target.checked)}
                  />
                }
                label={t("Birthday-only voucher")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t("Birthday Range (days)")}
                type="number"
                value={data?.birthdayRangeDays || ""}
                onChange={(e) =>
                  onChange("birthdayRangeDays", Number(e.target.value))
                }
                fullWidth
                helperText={t(
                  "How many days before/after birthday the voucher is valid"
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={data?.isFreeShipping || false}
                    onChange={(e) =>
                      onChange("isFreeShipping", e.target.checked)
                    }
                    color="primary"
                  />
                }
                label={t("Free Shipping")}
              />
              <Typography variant="caption" color="text.secondary" ml={6}>
                {t("Enable this if the coupon provides free shipping")}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {data?.couponId ? t("Update") : t("Create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CouponForm;
